import React, { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import KogCheckBoxControlled from "@/main/components/shared/checkbox/checkBoxControlled";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KOGImage from "@/main/components/shared/kogImage/kogImage";
import WrapperQuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import { KeyValueDefaultEnum } from "@/main/models/common/keyValueDefaultEnum";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { useReasonReducer } from "@/main/store/reason";

import { IFormValues, ImageInfo } from "../modalBulkReturnClaim";
import { IPartReturn, ISupplierReturn } from "../types";
import { getNameFormPart, getNameFormSupplier } from "../utilsmodalBulkReturnClaim";
import * as S from "./styles";
import { Collapse } from "@mui/material";
import KOGFileUploader from "../../fileUploader/fileUploader";
import Image from "@mui/icons-material/Collections";
import { useVisualIdentityReducer } from "@/main/store/visualIdentity";

interface ISupplierBulkReturnClaimProps {
	indexSupplier: number;
	supplier: ISupplierReturn;
	setImagesGeneral: React.Dispatch<React.SetStateAction<ImageInfo[]>>;
	imagesGeneral: ImageInfo[];
}
function SupplierBulkReturnClaim(props: ISupplierBulkReturnClaimProps) {
	const { reason: reasonReducer } = useReasonReducer();
	const { control, getValues, clearErrors, setValue } = useFormContext<IFormValues>();
	const watcherReason = useWatch({
		control,
		name: getNameFormSupplier("reason", props.indexSupplier),
	}) as KeyValueDefaultEnum | null;

	function handleToggleCheckboxSupplier(checked: boolean) {
		props.supplier.updateableParts.forEach((part, indexPart) => {
			const nameFormPart = getNameFormPart("isSelected", props.indexSupplier, indexPart);
			setValue(nameFormPart, checked);
		});

		const parts = getValues(`suppliers.${props.indexSupplier}.updateableParts`);
		const isUnselectedAll = parts.every((part) => !part.isSelected);

		if (isUnselectedAll) {
			const nameReasonFormSupplier = getNameFormSupplier("reason", props.indexSupplier);
			const nameReasonDescFormSupplier = getNameFormSupplier("reasonDescription", props.indexSupplier);

			setValue(nameReasonFormSupplier, null);
			setValue(nameReasonDescFormSupplier, "");
			clearErrors([nameReasonFormSupplier, nameReasonDescFormSupplier]);
		}
	}

	const hasSomePartSelected = () => {
		const parts = getValues(getNameFormSupplier("updateableParts", props.indexSupplier)) as IPartReturn[];
		const hasSomePartSelected = parts.some((part) => part.isSelected);

		return hasSomePartSelected;
	};

	const showReasonDescription = !isNullUndefined(watcherReason) && convertStringForBoolean(watcherReason.default);

	return (
		<S.ContainerProcurement>
			<S.ContainerSupplier>
				<S.SupplierName>{props.supplier.supplierName}</S.SupplierName>
				<KogCheckBoxControlled
					control={control}
					label=""
					disabled={false}
					nameCustom={getNameFormSupplier("isSelectedAll", props.indexSupplier)}
					onChangeCustom={handleToggleCheckboxSupplier}
				/>
			</S.ContainerSupplier>
			{props.supplier.updateableParts.map((part, indexPart) => (
				<PartSupplierBulkReturnClaim
					key={`${props.indexSupplier}-${part.guidProcurementSupplierItem}`}
					part={part}
					indexPart={indexPart}
					{...props}
				/>
			))}

			<S.AreaReasonSupplier>
				<KogAutoCompleteControlled
					id={`id-${getNameFormSupplier("reason", props.indexSupplier)}`}
					nameCustom={getNameFormSupplier("reason", props.indexSupplier)}
					label={getTranslateByKey("information.reason")}
					isDefaultValueUnique
					required={hasSomePartSelected()}
					rulesCustom={{ required: hasSomePartSelected() }}
					disabled={!hasSomePartSelected()}
					control={control}
					setValueCustom={setValue}
					options={reasonReducer.data}
					isLoading={reasonReducer.isLoading}
					style={{ margin: "1rem 0" }}
					onChangeCustom={() => {}}
				/>
				{showReasonDescription && (
					<KogInputTextControlledNew
						id="id-searchClaim-input-description"
						label={getTranslateByKey("information.description")}
						name={getNameFormSupplier("reasonDescription", props.indexSupplier)}
						control={control}
						required={showReasonDescription}
					/>
				)}
			</S.AreaReasonSupplier>
		</S.ContainerProcurement>
	);
}

interface IPartSupplierBulkReturnClaimProps extends ISupplierBulkReturnClaimProps {
	part: IPartReturn;
	indexPart: number;
}
function PartSupplierBulkReturnClaim({
	part,
	indexSupplier,
	indexPart,
	setImagesGeneral,
	imagesGeneral,
}: IPartSupplierBulkReturnClaimProps) {
	const { control, getValues, clearErrors, setValue } = useFormContext<IFormValues>();

	const {
		visualIdentity: { colors },
	} = useVisualIdentityReducer();

	const [images, setImages] = useState<File[]>([]);
	const [openCollapseIndex, setOpenCollapseIndex] = useState<boolean>(false);

	function handleToggleCheckbox(checked: boolean) {
		const parts = getValues(`suppliers.${indexSupplier}.updateableParts`);
		const nameFormPart = getNameFormPart("isSelected", indexSupplier, indexPart);
		setValue(nameFormPart, checked);

		const isSelectedAll = parts.every((part) => part.isSelected);
		const nameFormSupplier = getNameFormSupplier("isSelectedAll", indexSupplier);
		setValue(nameFormSupplier, isSelectedAll);

		const isUnselectedAll = parts.every((part) => !part.isSelected);

		if (isUnselectedAll) {
			const nameReasonFormSupplier = getNameFormSupplier("reason", indexSupplier);
			const nameReasonDescFormSupplier = getNameFormSupplier("reasonDescription", indexSupplier);

			setValue(nameReasonFormSupplier, null);
			setValue(nameReasonDescFormSupplier, "");
			clearErrors([nameReasonFormSupplier, nameReasonDescFormSupplier]);
		}
	}

	const handleClick = (supplierItem: string) => {
		const filteredImages = imagesGeneral
			.filter((item) => item.id === supplierItem)
			.reduce((acc, item) => [...acc, ...item.files], [] as File[]);

		setImages(filteredImages);
		setOpenCollapseIndex((prev) => !prev);
	};

	return (
		<S.ContainerPart>
			<S.Column customWidth="16%">
				<S.BoxImagePart>
					<S.ButtonImage type="button" onClick={() => handleClick(part.guidProcurementSupplierItem)}>
						<KOGImage src={part.partImage} />
					</S.ButtonImage>
				</S.BoxImagePart>
				<WrapperQuoteProcurementPartInfo data={part.quoteProcurementPartInfo} renderType="column" />
				<ColumnLabel label={""} content={<S.Quantity> {part.partQuantity}</S.Quantity>} />
				<S.ButtonImage type="button" onClick={() => handleClick(part.guidProcurementSupplierItem)}>
					<Image id="id-searchClaimUserGroup-icon-image" style={{ color: colors.pantone2726C }} />
				</S.ButtonImage>
				<S.AreaCheckboxPart>
					<KogCheckBoxControlled
						control={control}
						label=""
						disabled={false}
						nameCustom={getNameFormPart("isSelected", indexSupplier, indexPart)}
						onChangeCustom={handleToggleCheckbox}
					/>
				</S.AreaCheckboxPart>
			</S.Column>
			<Collapse in={openCollapseIndex}>
				<KOGFileUploader
					isFullScreenDragDrop={true}
					files={images}
					setFiles={setImages}
					onCustomInsert={(newFiles) => {
						const id = part.guidProcurementSupplierItem;

						const existingIndex = imagesGeneral.findIndex((item) => item.id === id);

						if (existingIndex !== -1) {
							setImagesGeneral((prevData) => {
								const updatedData = [...prevData];
								updatedData[existingIndex].files = [...updatedData[existingIndex].files, ...newFiles];
								return updatedData;
							});
						} else {
							const newImageData = {
								id,
								files: newFiles,
							};
							setImagesGeneral((prevData) => [...prevData, newImageData]);
						}
					}}
					onCustomDelete={(nameFile) => {
						setImagesGeneral((prevData) => {
							const updatedData = prevData.map((item) => ({
								id: item.id,
								files: item.files.filter((file) => file.name !== nameFile),
							}));
							return updatedData;
						});

						setImages((prevImages) => prevImages.filter((file) => file.name !== nameFile));
					}}
					typeFiles={"image"}
				/>
			</Collapse>
		</S.ContainerPart>
	);
}

export default SupplierBulkReturnClaim;
